// Lib
import { capitalize } from 'lodash/fp';
import { mergeAttributes, Node } from '@tiptap/core';
import { nodePasteRule, ReactNodeViewRenderer } from '@tiptap/react';
import InlineBoardLinkNodeView from './InlineBoardLinkNodeView';
import { isTiptapEditorEmpty } from '../../utils/isTiptapEditorEmpty';
import { InlineBoardLinkOptions, InlineBoardLinkStatus } from './inlineBoardLinkConstants';
import { DEFAULT_TIPTAP_EXTENSION_PRIORITY } from '../../tiptapTypes';
import { createAddSpaceAfterInlineNodePlugin } from './addSpaceAfterInlineNodePlugin';
import { createPasteTrackingPlugin } from '../trackPastePlugin';
import { getPositionBeforeSpaces } from '../../utils/getPositionBeforeSpaces';
import { isNil } from 'lodash';

interface InlineBoardLinkStorage {
    regex: RegExp;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        inlineBoardLink: {
            /**
             * Prevents the default backspace behaviour when there's a space before the cursor and
             * an inline node (like an inline board link) precedes the space.
             * The default behaviour would be to delete the inline node, whereas it should only
             * delete the space.
             */
            handleInlineNodeBackspace: () => ReturnType;
        };
    }
}

/**
 * Matches the URL of a Milanote board link, and groups the board ID, slug, and permission ID.
 */
const createRegex = (urlOrigin: string) => {
    const originRegex = urlOrigin
        // Match http or https optionally
        .replace(/https?\/\//, '(?:https?:\\/\\/)(?:')
        // Allow links to work in both staging and app environments but don't create a new group
        .replace(/(app|staging)/g, '(?:app|staging)')
        // Make '.' match exactly that character
        .replace(/\./g, '\\.');

    return new RegExp(`${originRegex}/([12][0-9a-zA-Z]{13})/?([^?\\s]*)(?:\\?p=)?([a-zA-Z0-9]{11})?`, 'g');
};

export const InlineBoardLink = Node.create<InlineBoardLinkOptions, InlineBoardLinkStorage>({
    name: 'inlineBoardLink',
    group: 'inline',
    inline: true,
    atom: true,
    // Makes the node selected if moving the cursor over it (i.e. pressing the arrow keys without holding shift)
    selectable: true,

    // Ensure the backspace handler is called before the default backspace handler
    priority: DEFAULT_TIPTAP_EXTENSION_PRIORITY + 1,

    addStorage() {
        return {
            regex: createRegex(this.options.urlOrigin || ''),
        };
    },

    addAttributes() {
        return {
            url: { default: '' },
            boardId: { default: '' },
            title: { default: '' },
            color: { default: '' },
            permissionId: { default: '' },
            status: { default: InlineBoardLinkStatus.initial },
        };
    },

    /**
     * If the HTML is copy and pasted into the editor, this will parse the HTML and extract the
     * attributes for the inline board link.
     */
    parseHTML() {
        return [
            {
                tag: 'span.inline-board-link',
                getAttrs: (element) => {
                    const a = element.querySelector('a');
                    if (!a) return false;

                    const boardId = a.getAttribute('data-board-id');
                    const url = a.getAttribute('href');
                    const title = a.querySelector('.board-title')?.textContent || '';
                    const color = (a.querySelector('.board-icon') as HTMLAnchorElement)?.style.backgroundColor || '';
                    const permissionId = a.getAttribute('data-permission-id') || '';

                    return {
                        url,
                        boardId,
                        title,
                        color,
                        permissionId,
                        status: InlineBoardLinkStatus.initial,
                    };
                },
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, { class: 'inline-board-link' }),
            [
                'a',
                {
                    href: node.attrs.url,
                    target: '_blank',
                    rel: 'noopener noreferrer nofollow',
                    'data-board-id': node.attrs.boardId,
                    'data-permission-id': node.attrs.permissionId || undefined,
                },
                [
                    'span',
                    {
                        class: 'board-icon',
                        style: `background-color: ${node.attrs.color};`,
                    },
                ],
                [
                    'span',
                    {
                        class: 'board-title',
                    },
                    node.attrs.title,
                ],
            ],
        ];
    },

    addCommands() {
        return {
            handleInlineNodeBackspace:
                () =>
                ({ state, dispatch }) => {
                    const { selection, tr, doc } = state;

                    if (!selection.empty) return false;

                    const { $head } = selection;

                    const textBeforeCursor = doc.textBetween($head.pos - 1, $head.pos, undefined, 'N/A');

                    // If the text before the cursor is a space
                    if (textBeforeCursor !== ' ') return false;

                    // Get the position before the spaces
                    const posBeforeSpaces = getPositionBeforeSpaces(doc, $head.pos);

                    // If the previous node is an inline board link
                    const nodeBeforeSpaces = doc.nodeAt(posBeforeSpaces - 1);

                    if (nodeBeforeSpaces?.type.name !== this.name) return false;

                    // Prevent the default backspace behaviour and simply delete a single space
                    //  otherwise, all the spaces and the inline node would be deleted
                    tr.delete($head.pos - 1, $head.pos);

                    if (dispatch) dispatch(tr);

                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            Backspace: () => this.editor.commands.handleInlineNodeBackspace(),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(InlineBoardLinkNodeView);
    },

    addPasteRules() {
        return [
            nodePasteRule({
                find: (text) => {
                    if (isTiptapEditorEmpty(this.editor)) return null;

                    return [...text.matchAll(this.storage.regex)].map((match) => {
                        const [textUrl, boardId, slug, permissionId] = match;

                        return {
                            index: match.index,
                            text: textUrl,
                            match,
                            data: {
                                url: textUrl,
                                boardId,
                                title: slug
                                    ? capitalize(decodeURIComponent(slug.replace(/-/g, ' ')))
                                    : 'Milanote Board',
                                permissionId,
                            },
                        };
                    });
                },
                type: this.type,
                getAttributes: (match) => ({ ...match.data, status: InlineBoardLinkStatus.initial }),
            }),
        ];
    },

    // Add plugins to track paste insertions and add a space after the inline board link when it
    // is created via paste.
    addProseMirrorPlugins() {
        const { onInsertedFromPaste, addSpaceOnCreation } = this.options;

        return [
            addSpaceOnCreation ? createAddSpaceAfterInlineNodePlugin(this.name) : null,
            onInsertedFromPaste ? createPasteTrackingPlugin(this.name, onInsertedFromPaste) : null,
        ].filter((plugin) => !isNil(plugin));
    },
});
